<template>
  <v-container class="pa-0">
    <!-- Premium Header -->
    <v-sheet color="primary" class="pa-6 d-flex align-center">
      <v-avatar size="40" color="white opacity-20" class="mr-4">
        <v-icon color="white" small>fas fa-clipboard-check</v-icon>
      </v-avatar>
      <div>
        <div class="text-h5 white--text font-weight-black">Reporte de Producción</div>
        <div class="caption white--text opacity-70">Registre el avance y fin de los procesos productivos</div>
      </div>
      <v-spacer></v-spacer>
      <v-btn icon color="white" @click="$emit('close')"><v-icon small>fas fa-times</v-icon></v-btn>
    </v-sheet>

    <ValidationObserver v-slot="{ handleSubmit }">
      <v-form @submit.prevent="handleSubmit(save)" class="pa-6">
        <!-- Main Products Table -->
        <v-card outlined class="modern-card overflow-hidden mb-6">
          <v-simple-table class="modern-table">
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-center grey lighten-5" width="80">OT</th>
                  <th class="text-left grey lighten-5">Producto / Descripción</th>
                  <th class="text-left grey lighten-5" width="180">Progreso Actual</th>
                  <th class="text-left grey lighten-5" width="180" v-if="machine.production_unit_m2">Producción (m²)</th>
                  <th class="text-left grey lighten-5" width="180" v-else>Producción (Un)</th>
                  <th class="text-center grey lighten-5" width="120" v-if="machine.hour_control">Tiempos</th>
                  <th class="text-center grey lighten-5" width="100">Cerrar?</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="process in processes" :key="process.id">
                  <td class="text-center">
                    <v-chip x-small label class="font-weight-black grey lighten-4">#{{ process.work_order }}</v-chip>
                  </td>
                  <td>
                    <div v-if="process.product.library_product" class="caption font-weight-bold primary--text">
                      {{ process.product.library_product.sku }}
                    </div>
                    <div class="body-2 grey--text text--darken-4 text-truncate" style="max-width: 200px;">
                      {{ process.product.name }}
                    </div>
                  </td>
                  <!-- Progress Column -->
                  <td>
                    <div v-if="machine.production_unit_m2">
                      <div class="d-flex justify-space-between caption mb-1">
                        <span class="font-weight-bold">{{ process.completed_mts2_qty.mts2 | number('0.00') }}</span>
                        <span class="grey--text">/ {{ process.product.mts2 * (process.qtypp === 0 ? 1 : process.qtypp) | number('0.00') }}</span>
                      </div>
                      <v-progress-linear
                        color="primary"
                        height="4"
                        rounded
                        :value="process.completed_mts2_qty.mts2 / (process.product.mts2 * (process.qtypp === 0 ? 1 : process.qtypp)) * 100"
                      />
                    </div>
                    <div v-else>
                      <div class="d-flex justify-space-between caption mb-1">
                        <span class="font-weight-bold">{{ process.completed_mts2_qty.qty | number('0.00') }}</span>
                        <span class="grey--text">/ {{ process.product.qty * (process.qtypp === 0 ? 1 : process.qtypp) + process.merma | number('0.00') }}</span>
                      </div>
                      <v-progress-linear
                        color="primary"
                        height="4"
                        rounded
                        :value="process.completed_mts2_qty.qty / (process.product.qty * (process.qtypp === 0 ? 1 : process.qtypp) + process.merma) * 100"
                      />
                    </div>
                  </td>
                  <!-- Input Column -->
                  <td class="pt-4">
                    <ValidationProvider name="Producción" rules="required" v-slot="{ errors }">
                      <v-text-field
                        v-if="machine.production_unit_m2"
                        v-model="process.real_mts2"
                        append-icon="fas fa-ruler-combined"
                        outlined dense hide-details="auto"
                        class="modern-input"
                        type="number"
                        :error-messages="errors"
                        @input="calculateQty(process)"
                      />
                      <v-text-field
                        v-else
                        v-model="process.real_qty"
                        append-icon="fas fa-layer-group"
                        outlined dense hide-details="auto"
                        class="modern-input"
                        type="number"
                        :error-messages="errors"
                        @input="calculateMts2(process)"
                      />
                    </ValidationProvider>
                  </td>
                  <!-- Time Column -->
                  <td v-if="machine.hour_control">
                    <div class="d-flex flex-column gap-1 py-2">
                      <v-text-field v-model="process.start" label="Inicio" type="datetime-local" outlined dense hide-details class="modern-input-mini" />
                      <v-text-field v-model="process.end" label="Fin" type="datetime-local" outlined dense hide-details class="modern-input-mini" />
                    </div>
                  </td>
                  <!-- Close Checkbox -->
                  <td class="text-center">
                    <v-checkbox v-model="process.ask_completed" color="success" hide-details class="mt-0 pt-0 d-inline-block" />
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card>

        <!-- Summary and Global Fields -->
        <v-row>
          <v-col cols="12" md="4">
            <div class="pa-6 rounded-xl bg-primary-light border-primary-light h-100 text-center d-flex flex-column justify-center align-center">
              <div class="caption primary--text font-weight-black text-uppercase mb-1">Resumen del Lote</div>
              <div class="text-h3 font-weight-black primary--text">
                {{ (machine.production_unit_m2 ? mts2 : qty) | number('0.00') }}
              </div>
              <div class="caption font-weight-bold grey--text">{{ machine.production_unit_m2 ? 'METROS CUADRADOS' : 'UNIDADES TOTALES' }}</div>
            </div>
          </v-col>
          
          <v-col cols="12" md="8">
            <v-card outlined class="modern-card pa-6 h-100">
              <v-row dense>
                <v-col cols="12">
                  <ValidationProvider name="Máquina" rules="required" v-slot="{ errors }">
                    <v-select
                      label="Equipo Utilizado"
                      v-model="equipment"
                      :items="machine.equipments"
                      item-text="name"
                      item-value="id"
                      outlined dense hide-details="auto"
                      class="modern-select mb-4"
                      :error-messages="errors"
                      prepend-inner-icon="fas fa-tools"
                      placeholder="Seleccione el equipo responsable"
                    />
                  </ValidationProvider>
                </v-col>
                <v-col cols="12">
                  <v-textarea
                    v-model="notes"
                    outlined dense hide-details="auto"
                    label="Notas e Incidencias"
                    class="modern-input"
                    placeholder="Escriba aquí observaciones del turno o del proceso..."
                    rows="3"
                  />
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>

        <!-- Form Actions -->
        <div class="d-flex justify-end pt-8 gap-3">
          <v-btn text color="grey darken-1" class="font-weight-bold px-8" @click="$emit('close')">Cancelar</v-btn>
          <v-btn type="submit" color="primary" depressed class="font-weight-bold px-10 shadow-sm" :loading="loading">
            <v-icon small class="mr-2">fas fa-paper-plane</v-icon> Registrar Reporte
          </v-btn>
        </div>
      </v-form>
    </ValidationObserver>
  </v-container>
</template>

<script>
import { postProcessOrderComplete } from '@/api/production'
import notifications from '@/utils/notifications'

export default {
  name: 'ProcessCompleteConventional',
  props: ['processes', 'machine'],
  data() {
    return {
      equipment: '',
      mts2: 0,
      qty: 0,
      loading: false,
      notes: '',
    }
  },
  methods: {
    async save() {
      this.loading = true
      const proccessesToComplete = this.processes.map(p => ({
        id: parseInt(p.id),
        real_mts2: parseFloat(p.real_mts2) || 0,
        real_qty: parseFloat(p.real_qty) || 0,
        ask_completed: Boolean(p.ask_completed),
        start: p.start ? new Date(p.start).toISOString() : new Date().toISOString(),
        end: p.end ? new Date(p.end).toISOString() : new Date().toISOString(),
      }))

      if (this.machine.hour_control) {
        const invalidDates = proccessesToComplete.filter(p => (new Date(p.start) > new Date(p.end)))
        if (invalidDates.length > 0) {
          notifications.showError('La fecha de término no puede ser anterior al inicio')
          this.loading = false
          return
        }
      }

      try {
        const res = await postProcessOrderComplete({
          processes: proccessesToComplete,
          equipment: this.equipment,
          notes: this.notes
        })
        notifications.showSuccess('Proceso registrado con éxito')
        this.$emit('refresh')
        this.$emit('close')
      } catch (e) {
        notifications.showError('Error al registrar: ' + e)
      } finally {
        this.loading = false
      }
    },
    calculateQty(item) {
      const productArea = parseFloat(item.product.width) * parseFloat(item.product.height) * 0.0001
      item.real_qty = parseFloat(item.real_mts2) / productArea
      this.suma()
    },
    calculateMts2(item) {
      const productArea = parseFloat(item.product.width) * parseFloat(item.product.height) * 0.0001
      item.real_mts2 = parseFloat(item.real_qty) * productArea
      this.suma()
    },
    suma() {
      this.qty = 0
      this.mts2 = 0
      this.processes.forEach(proc => {
        this.qty += parseFloat(proc.real_qty || 0)
        this.mts2 += parseFloat(proc.real_mts2 || 0)
      })
    },
  },
  watch: { 
    processes: {
      handler() { this.suma() },
      immediate: true
    }
  },
  computed: {
    hour_control() {
      return this.machine.hour_control
    }
  }
}
</script>

<style scoped>
.modern-card { border: 1px solid rgba(0,0,0,0.08) !important; border-radius: 12px !important; }
.bg-primary-light { background-color: rgba(var(--v-primary-base), 0.05); }
.border-primary-light { border: 1px solid rgba(var(--v-primary-base), 0.1); }
.opacity-20 { background-color: rgba(255,255,255,0.2) !important; }
.opacity-70 { opacity: 0.7; }
.shadow-sm { box-shadow: 0 4px 12px rgba(0,0,0,0.1) !important; }
.gap-1 { gap: 4px; }
.gap-3 { gap: 12px; }
.modern-input-mini >>> .v-input__slot { min-height: 32px !important; font-size: 0.75rem !important; }
.modern-input-mini >>> .v-label { font-size: 0.75rem !important; }
.modern-table >>> td { padding: 12px 16px !important; }
</style>